/**
 * The derived state of one queue row, shared between the table (which counts,
 * filters and selects rows) and the row itself (which renders the same verdict).
 *
 * Kept in one place because the two must agree exactly: a row the table counts
 * as Ready but the row renders as blocked is a queue whose own totals lie.
 */

/** Money comparisons are made to the half-cent, so a float product never reads as a mismatch. */
const CENT = 0.005;

/**
 * PurchaseOrder::STATUS_* — the raw ints the resource reports, deliberately
 * unlabelled there so the screen decides the wording.
 *
 * APPROVED is listed even though approving a commitment now clears its row's flag
 * and drops it out of the queue: rows committed before that behaviour existed are
 * still flagged and still carry an approved order, and reporting one of those as
 * awaiting approval would be a straight lie about work that is already done.
 */
const toNumber = (value) => {
    const numeric = Number(value);

    return Number.isFinite(numeric) ? numeric : 0;
};

export const COMMITMENT_PENDING = 0;
export const COMMITMENT_APPROVED = 1;
export const COMMITMENT_REJECTED = 2;
export const COMMITMENT_CANCELED = 3;

/**
 * The unedited row: the price/quantity split the server proposed, unsigned, owned
 * by whoever is looking at the queue.
 *
 * A row whose budget line carries no price has no proposal to seed from, so both
 * inputs start empty rather than at zero — zero is a figure someone chose.
 */
export const defaultEdit = (item, currentUserId) => ({
    // No `price`: it is locked to the budget line's own price and read straight off
    // the item, so there is nothing here for the reconciler to have changed.
    quantity: item.proposed_quantity ?? '',
    signed: false,
    ownerId: currentUserId ?? null,
    // Null means "untouched": the row is submitted without a cashflow and the
    // server applies its own single-entry default. Only a reconciler who opened
    // the pop-up and applied something puts an array here.
    cashflow: null,
});

/** The date part of the actual's timestamp, which is what a cashflow entry is dated from. */
export const actualDate = (item) => (item.date ? String(item.date).slice(0, 10) : '');

/**
 * What the pop-up opens on: the actual's own payment, plus the balance if the row
 * commits more than was spent.
 *
 * The first entry is the actual on its own date and is not the reconciler's to
 * change — it is what the transaction says happened. The second exists only when the
 * commitment is worth more, and carries the difference; it is dated in the actual's
 * month because the balance has no date of its own and that is the nearest honest
 * guess. Both are theirs to spread further, so long as the total still comes back to
 * the commit value.
 *
 * Returns the single entry when the two are equal, so a row committing exactly its
 * actual opens on exactly what the server would raise unaided.
 */
export const defaultCashflow = (item, committedValue = null) => {
    const amount = toNumber(item.amount);
    const committed = committedValue === null ? amount : toNumber(committedValue);
    const entries = [{ date: actualDate(item), amount, locked: true }];
    const balance = committed - amount;

    if (balance > CENT) {
        entries.push({ date: actualDate(item), amount: balance });
    }

    return entries;
};

export const cashflowTotal = (entries) => (entries ?? []).reduce((sum, entry) => sum + toNumber(entry.amount), 0);

/**
 * Whether a set of entries adds up to what the commitment is worth.
 *
 * The backend stores each entry as a PERCENTAGE of the commitment, so a set that
 * does not sum to the whole is not merely wrong, it is unrepresentable — which is
 * why this gates Apply outright rather than warning.
 */
export const cashflowBalanced = (entries, target) => Math.abs(cashflowTotal(entries) - toNumber(target)) <= CENT;

/**
 * @param {object} item  The row as SendForCommitmentAllocationResource sent it.
 * @param {object} edit  The reconciler's unsaved changes to it.
 */
export const rowState = (item, edit) => {
    // The price is not the reconciler's to set: it is the budgeted price, and the
    // only way to a different one is to change the budget line. `edit.price` is
    // deliberately not read — the quantity is the whole of what this screen decides.
    const price = toNumber(item.budgeted_price);
    const quantity = toNumber(edit.quantity);
    const committed = price * quantity;
    const amount = toNumber(item.amount);

    // A null workflow means the budget line cannot be procured at all — a Position,
    // the synthetic Total Workforce marker, or an Expense of a non-procurable type.
    const procurable = item.workflow !== null && item.workflow !== undefined;

    // Only a LIVE order stands for a commitment this row already has. A pending or
    // approved one leaves nothing to decide here — the figures are fixed at what
    // was submitted, and the server refuses a second commitment from the same
    // allocation. A rejected or cancelled one is a commitment that did not happen:
    // the row comes back to the reconciler to be raised again, exactly as
    // CommitFromActualsService::gateFailure() reads it at submit. Blocking those
    // would strand the only rows on the screen that need action.
    const commitment = item.commitment ?? null;
    const commitmentStatus = commitment ? toNumber(commitment.status) : null;
    const submitted = commitmentStatus === COMMITMENT_PENDING || commitmentStatus === COMMITMENT_APPROVED;
    const returned = commitmentStatus === COMMITMENT_REJECTED || commitmentStatus === COMMITMENT_CANCELED;

    const uncommittedKnown = item.uncommitted !== null && item.uncommitted !== undefined;
    const uncommitted = toNumber(item.uncommitted);

    // What the line would have left if this row committed what it currently says.
    // Recomputed live rather than read from the server's `uncommitted_after`, because
    // the quantity is now the reconciler's to move and the figure has to follow it.
    const uncommittedAfter = uncommittedKnown ? uncommitted - committed : null;

    // The commit value may exceed the actual — that is the point of an editable
    // quantity — but never fall below it: the commitment has to at least cover the
    // money that was actually spent.
    const belowActual = committed < amount - CENT;

    // ⚠️ THE ONE BOUND STILL WITH THE CLIENT. Committing past what the line has left
    // is either refused outright or routed through the budget change process, and the
    // two readings of the requirement disagree. Both answers turn on this single
    // predicate: whether it feeds `blocked` (refuse) or only the badge (route).
    const aboveUncommitted = uncommittedKnown && committed > uncommitted + CENT;

    // Currently NOT blocking, matching the behaviour that is live: the excess goes
    // through the budget change process and the row stays submittable. Moving it into
    // `blocked` below is the whole of the change if it becomes a refusal.
    const blocked = submitted || !procurable;

    // Raising the budget line is now the only route to a different price as well as to
    // more headroom, so the link is offered wherever either would help.
    const editableShortfall = aboveUncommitted && procurable && !submitted;

    // An applied cashflow is a set of shares of the COMMIT VALUE, not of the actual —
    // raising the quantity raises what the schedule has to add up to. Now reachable:
    // the commit value moves independently of the cashflow, so a schedule applied at
    // one value and then out-grown by another is an ordinary thing to do.
    const cashflow = edit.cashflow ?? null;
    const cashflowAdjusted = Array.isArray(cashflow) && cashflow.length > 0;
    const cashflowStale = cashflowAdjusted && !cashflowBalanced(cashflow, committed);

    // Committing more than the actual leaves a balance with no date of its own. The
    // pop-up pre-populates it, but the reconciler has to look at it and apply it —
    // until they do, the row is not ready, which is what puts it under Needs
    // attention. Bringing the quantity back down to the actual clears the balance and
    // with it this state.
    const balanceUnapplied = committed > amount + CENT && !cashflowAdjusted;

    return {
        price,
        quantity,
        committed,
        procurable,
        belowActual,
        aboveUncommitted,
        editableShortfall,
        submitted,
        returned,
        commitmentStatus,
        blocked,
        uncommitted: uncommittedKnown ? uncommitted : null,
        uncommittedAfter,
        cashflow,
        cashflowAdjusted,
        cashflowStale,
        balanceUnapplied,
        ready: !blocked && !belowActual && !cashflowStale && !balanceUnapplied,
    };
};

/**
 * What makes two rows the same commitment, so they may be combined into one.
 *
 * SubmitForCommitmentRequest decides this on `valuable_type`, `valuable_id` and
 * the lower-cased supplier name, and rejects a group spanning two of either with
 * a 422 over the WHOLE request — every well-formed row submitted alongside it is
 * lost. So this must never group two rows the server would call different.
 *
 * The row payload carries no budget line identity, only its name, and two lines
 * in one event can share a name. The three figures read off the Expense are added
 * to the key to tell those apart: every row on one budget line reports the same
 * budgeted price, budgeted quantity and uncommitted balance, because all three are
 * functions of that Expense alone — so they can only ever split a match that was
 * about to be wrong, never split a real one. Drop them the moment the resource
 * reports the budget line's identity.
 */
export const combineKey = (item) =>
    [
        String(item.supplier ?? '')
            .trim()
            .toLowerCase(),
        String(item.budget_line ?? ''),
        String(item.budgeted_price ?? ''),
        String(item.budgeted_quantity ?? ''),
        String(item.uncommitted ?? ''),
    ].join('|');

/**
 * The selected rows bucketed by the commitment they would raise.
 *
 * @param {Array<object>} selectedRows  `{ item, edit, state }` in display order.
 * @return {Map<string, Array<object>>}
 */
const bucketByCombineKey = (selectedRows) => {
    const buckets = new Map();

    selectedRows.forEach((row) => {
        const key = combineKey(row.item);
        const bucket = buckets.get(key);

        if (bucket) {
            bucket.push(row);
        } else {
            buckets.set(key, [row]);
        }
    });

    return buckets;
};

/**
 * The groups the reconciler has combined, as the screen and the payload read them.
 *
 * A group is only honoured while it still holds two or more selected rows: one row
 * is not a combination, it is the ordinary single-row commitment, and sending it a
 * `combine_group` of its own would say something the server would only undo. So a
 * group whose other rows were deselected dissolves on its own, with nothing to
 * keep in sync.
 *
 * `total` is what the commitment is raised at and what the budget gate measures,
 * which is why it is reported per group rather than per row: three rows that each
 * fit on their own can still overspend the line between them, and the server
 * refuses the whole group where they do.
 *
 * @param {Array<object>} selectedRows  `{ item, edit, state }` in display order.
 * @param {object} assignments  allocation id → group id.
 */
export const combineGroupState = (selectedRows, assignments) => {
    const members = new Map();

    selectedRows.forEach((row) => {
        const groupId = assignments[row.item.id];

        if (!groupId) {
            return;
        }

        const bucket = members.get(groupId);

        if (bucket) {
            bucket.push(row);
        } else {
            members.set(groupId, [row]);
        }
    });

    const groups = [];
    const byRow = {};

    // Numbered in the order the groups first appear on screen, so "Group 2" is
    // always the second one down the table rather than the second one created.
    [...members.entries()]
        .filter(([, rows]) => rows.length > 1)
        .sort(([, a], [, b]) => selectedRows.indexOf(a[0]) - selectedRows.indexOf(b[0]))
        .forEach(([id, rows], index) => {
            const total = rows.reduce((sum, row) => sum + row.state.committed, 0);
            // Every row of a group commits against the same budget line, so they
            // all report the same balance and the first is as good as any.
            const uncommitted = rows[0].item.uncommitted;
            const known = uncommitted !== null && uncommitted !== undefined;

            const group = {
                id,
                number: index + 1,
                size: rows.length,
                total,
                uncommittedAfter: known ? toNumber(uncommitted) - total : null,
                exceeds: known && total > toNumber(uncommitted) + CENT,
            };

            groups.push(group);
            rows.forEach((row) => {
                byRow[row.item.id] = group;
            });
        });

    return { groups, byRow };
};

/**
 * The selected rows that can still be combined with another: ids only, grouped
 * into the buckets a "combine" would create.
 *
 * A bucket already wholly inside one group is not offered again — there is nothing
 * left to add to it — but one holding a group plus a loose row is, because
 * combining then folds the loose row in.
 *
 * @param {Array<object>} selectedRows  `{ item, edit, state }` in display order.
 * @param {object} groupByRow  allocation id → group, as combineGroupState reports it.
 * @return {Array<Array<number>>} one entry of allocation ids per combinable bucket.
 */
export const combinableBuckets = (selectedRows, groupByRow) => {
    const buckets = [];

    bucketByCombineKey(selectedRows).forEach((rows) => {
        if (rows.length < 2) {
            return;
        }

        const groupIds = new Set(rows.map((row) => groupByRow[row.item.id]?.id ?? null));

        if (groupIds.size === 1 && !groupIds.has(null)) {
            return;
        }

        buckets.push(rows.map((row) => row.item.id));
    });

    return buckets;
};

/** Whether the row is offered to the search box's needle. */
export const matchesSearch = (item, needle) => {
    if (!needle) {
        return true;
    }

    const haystack = [item.supplier, item.description, item.budget_line].filter(Boolean).join(' ').toLowerCase();

    return haystack.includes(needle.toLowerCase());
};
